import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import TrainingBrochure, {
  TrainingBrochureForm,
} from "@/interfaces/TrainingBrochure";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useStoreTrainingBrochureMutation } from "@/services/trainingBrochures";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreTrainingBrochureModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<unknown, TrainingBrochure>
> = ({ onHide, callback }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const defaultAdditional = useRef<{ pageIndex: number }>({
    pageIndex: 0,
  }).current;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TrainingBrochureForm>();
  const [storeTrainingBrochureAsync] = useStoreTrainingBrochureMutation();
  const { loadOrganisationsOptions } = useLoadOptions();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<TrainingBrochureForm> = ({
    organisation,
    ...data
  }) => {
    toast.promise(
      storeTrainingBrochureAsync({
        ...data,
        organisationId: organisation?.id ?? null,
      }).unwrap(),
      {
      pending: "Ajout de la plaquette en cours...",
      success: {
        render({ data }) {
          showOrHiddenModal();
          reset();
          data && callback?.(data);
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              La plaquette a bien été ajoutée
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
      }
    );
  };

  const onError: SubmitErrorHandler<TrainingBrochureForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter une plaquette de formation"
      requiredLabel
      onHide={onHide}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Label <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Plaquette SST 2025"
              type="text"
              {...register("label", {
                required: {
                  value: true,
                  message: INVALID_NAME_MESSAGE,
                },
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.label}
            />
            <Form.Control.Feedback type="invalid">
              {errors.label?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Fichier PDF <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              {...register("file", {
                required: {
                  value: true,
                  message: "Le fichier est obligatoire",
                },
              })}
              isInvalid={!!errors.file}
            />
            <Form.Control.Feedback type="invalid">
              {errors.file?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>Organisme :</Form.Label>
            <Controller
              control={control}
              name="organisation"
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadOrganisationsOptions}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id.toString()}
                  value={field.value}
                  isClearable
                  onChange={field.onChange}
                />
              )}
            />
            <Form.Text className="text-muted">
              Associez cette plaquette à un organisme (optionnel)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreTrainingBrochureModal);
